import { Text } from 'bold-ui'
import { parseISO } from 'date-fns'
import { useAcompanhamentoPreNatalQuery } from 'graphql/hooks.generated'
import React, { useMemo } from 'react'
import { calcularIMC } from 'util/atendimento'
import {
  getMedicaoForImcReferencia,
  getMedicoesAntropometricasPreNatal,
} from 'view/atendimentos/detail/components/modals/medicoes/pre-natal/util-medicoesPreNatal'
import { PeriodoGestacaoModel } from 'view/atendimentos/detail/components/modals/types/PeriodoGestacaoModel'
import { CidadaoMedicoes } from 'view/atendimentos/detail/soap/aside/medicoes/model'
import { HistoricoMedicaoModel } from 'view/atendimentos/types/model-historicoMedicao'

import { TipoPreNatal } from '../../pre-natal/model-preNatal'
import { ContentCardSection } from '../components/ContentCardSection'
import { SubtitleCard } from '../components/SubtitleCard'
import { usePeriodoGestacional } from '../pre-natal/hooks/usePeriodoGestacional'
import { getDumAtual } from '../pre-natal/util/util-acompanhamentoPreNatal'
import { GrupoMedicoesTipoModel } from '../types/MedicaoModel'
import { MedicoesListCard } from './components/MedicoesListCard'
import { ImcPreGestacionalCardItem } from './ImcPreGestacionalCardItem'

export interface MedicoesCardReadOnlyProps {
  loading: boolean
  prontuarioId: ID
  dataAtendimento?: Instant
  historicoMedicoes: HistoricoMedicaoModel[]
  medicoesHoje: ReadonlyArray<GrupoMedicoesTipoModel>
  canRender: boolean
  cidadao: CidadaoMedicoes
  gestacoes: ReadonlyArray<PeriodoGestacaoModel>
  isGestante: boolean
  dumAtendimentoAtual?: LocalDate
  tipoPreNatal?: TipoPreNatal
}

export function MedicoesCardReadOnly(props: Readonly<MedicoesCardReadOnlyProps>) {
  const {
    loading,
    prontuarioId,
    dataAtendimento,
    historicoMedicoes,
    medicoesHoje,
    canRender,
    cidadao,
    gestacoes,
    isGestante,
    dumAtendimentoAtual,
    tipoPreNatal,
  } = props

  const {
    data: { atendimentosProfUltimaGestacao },
    loading: loadingAcompanhamentoPreNatal,
  } = useAcompanhamentoPreNatalQuery({
    variables: { input: prontuarioId },
  })

  const dumPersistida = getDumAtual(atendimentosProfUltimaGestacao)
  const dumAtendimentoAtualParsed = dumAtendimentoAtual && parseISO(dumAtendimentoAtual)

  const { dataInicioGestacao, loading: loadingPeriodoGestacional } = usePeriodoGestacional({
    prontuarioId,
    dum: dumPersistida,
    dumAtendimentoAtual: dumAtendimentoAtualParsed,
    dataAtendimento,
    skip: loadingAcompanhamentoPreNatal,
  })

  const medicoesAntropometricasPreNatal = useMemo(
    () => getMedicoesAntropometricasPreNatal(historicoMedicoes, tipoPreNatal),
    [historicoMedicoes, tipoPreNatal]
  )

  const medicaoForImcReferencia = useMemo(() => {
    const dataValida = dataInicioGestacao instanceof Date && !Number.isNaN(dataInicioGestacao.getTime())
    const medicoesValidas = Array.isArray(medicoesAntropometricasPreNatal) && !medicoesAntropometricasPreNatal.isEmpty()

    return dataValida && medicoesValidas
      ? getMedicaoForImcReferencia(dataInicioGestacao, [...medicoesAntropometricasPreNatal])
      : undefined
  }, [dataInicioGestacao, medicoesAntropometricasPreNatal])

  const imcReferencia = useMemo(() => {
    if (!medicaoForImcReferencia) return undefined

    const { peso, altura } = medicaoForImcReferencia
    if (peso != null && altura != null) {
      return calcularIMC(peso, altura)
    }

    return undefined
  }, [medicaoForImcReferencia])

  return (
    <ContentCardSection
      isLoading={loading || loadingPeriodoGestacional}
      messageLoading='Carregando dados das medições'
      canRender={canRender}
    >
      {!!imcReferencia && isGestante && <ImcPreGestacionalCardItem imc={imcReferencia} />}
      <SubtitleCard title='De hoje' />
      <MedicoesListCard medicoes={medicoesHoje} cidadao={cidadao} gestacoes={gestacoes} isGestante={isGestante} />
      {medicoesHoje.length === 0 && <Text component='p'>Nenhuma medição registrada hoje.</Text>}
    </ContentCardSection>
  )
}
